import {
  Injectable,
  Injector,
} from '@angular/core';
import { AccountService } from '../../core/account.service';
import {
  convertRecipientToUser,
  convertUserToRecipient,
} from '../interface';
import {
  distinctUntilChanged,
  EMPTY,
  firstValueFrom,
  iif,
  Observable,
  of,
  ReplaySubject,
  retry,
  Subject,
} from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  take,
} from 'rxjs/operators';
import { User } from '../../core/user';
import {
  CrmFieldService,
  PlatformExtensionFieldIds,
  StandardExternalIds,
} from '@galaxy/crm/services/field-service';
import {
  Association,
  CRMApiService,
  CRMAssociationApiService,
  CrmObject,
  FilterGroupOperator,
  FilterOperator,
  GetMultiCrmObjectResponse,
  ListAssociationsRequestInterface,
} from '@vendasta/crm';
import {
  createCrmObjectModalProviders,
  CrmCreateCrmObjectModalComponent,
  CrmCreateCrmObjectModalResult,
  CrmPlatformApiService,
  ListObjectTypeDialogData,
  ListObjectTypeDialogResult,
  PageAnalyticsInjectionToken,
  SelectObjectTypeModalComponent,
  tableFiltersInjectionTokenGenerator,
} from '@galaxy/crm/static';
import {
  AccountGroupService,
  ProjectionFilter,
  ReadFilter,
} from '@galaxy/account-group';
import { AddContactAsIamUserForCompanyStatus } from '@vendasta/crm-integrations';
import {
  GalaxyFilterChipInjectionToken,
  GalaxyFilterOperator,
} from '@vendasta/galaxy/filter/chips';
import {
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { AddUserDialogComponent } from '../../accounts/add-user-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Recipient,
  RecipientProvider,
} from '@galaxy/partner-center-client/billing/static/core/recipients';

interface AddRecipientToAccountRequest {
  accountGroupId: string;
  partnerId: string;
  // To filter out existing recipients on account when making a selection
  existingRecipientsOnAccount?: Recipient[];
}

interface AssociatedContact {
  contactId: string;
  namespace: string;
}

enum RecipientStrategy {
  CRMContact = 0,
  VBCUser = 1,
}

const RECIPIENT_STRATEGY: RecipientStrategy = RecipientStrategy.CRMContact;

@Injectable()
export class RecipientProviderService implements RecipientProvider<Recipient> {
  private readonly addRecipientInterfaceRequested$$ = new ReplaySubject<AddRecipientToAccountRequest>(1);

  private readonly contactAssociated$$ = new ReplaySubject<AssociatedContact>(1);
  private readonly contactAssociated$ = this.contactAssociated$$.pipe(distinctUntilChanged());

  private readonly selectionResult$$ = new Subject<Recipient>();

  constructor(
    private readonly accountService: AccountService,
    private readonly crmAssociationApiService: CRMAssociationApiService,
    private readonly crmAPIService: CRMApiService,
    private readonly accountGroupService: AccountGroupService,
    private readonly platformService: CrmPlatformApiService,
    private readonly fieldService: CrmFieldService,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly injector: Injector,
  ) {
    this.addRecipientInterfaceRequested$$.pipe(takeUntilDestroyed()).subscribe((req) => {
      if (RECIPIENT_STRATEGY === RecipientStrategy.CRMContact) {
        this.openAddCRMContactModal(req);
      } else {
        this.openUserModal(req);
      }
    });
    this.contactAssociated$.subscribe((contactId) => this.getContactAndSetResult(contactId));
  }

  private async getContactAndSetResult(associatedContact: AssociatedContact): Promise<void> {
    const recipient$ = this.crmAPIService
      .getMultiContact({
        namespace: associatedContact.namespace,
        crmObjectIds: [associatedContact.contactId],
      })
      .pipe(
        map((resp: GetMultiCrmObjectResponse) => {
          if (JSON.stringify(resp?.crmObjects?.[0]) === '{}') {
            throw false;
          }

          return resp?.crmObjects?.[0];
        }),
        retry({ delay: 1000 }),
        map((contact) => this.convertContactToRecipient(contact)),
        take(1),
      );

    await firstValueFrom(recipient$).then((recipient) => this.selectionResult$$.next(recipient));
  }

  private convertContactToRecipient(contact: CrmObject): Recipient {
    const userId =
      this.fieldService.getFieldValueFromCrmObject(contact, PlatformExtensionFieldIds.UserID)?.stringValue ?? '';

    const firstNameFieldId = this.fieldService.getFieldId(StandardExternalIds.FirstName);
    const firstName = contact?.fields?.find((field) => field.fieldId === firstNameFieldId)?.stringValue ?? '';

    const lastNameFieldId = this.fieldService.getFieldId(StandardExternalIds.LastName);
    const lastName = contact?.fields?.find((field) => field.fieldId === lastNameFieldId)?.stringValue ?? '';

    const emailFieldId = this.fieldService.getFieldId(StandardExternalIds.Email);
    const email = contact?.fields?.find((field) => field.fieldId === emailFieldId)?.stringValue ?? '';

    return {
      id: contact.crmObjectId,
      userID: userId,
      firstName: firstName,
      lastName: lastName,
      email: email,
    };
  }

  private addContactAsIAMUser(contactId: string, companyId: string, namespace: string): Observable<Recipient> {
    return this.platformService.addContactAsIamUserForCompany(contactId, companyId, false).pipe(
      map((response) => {
        if (!response) {
          return AddContactAsIamUserForCompanyStatus.ADD_CONTACT_AS_IAM_USER_FOR_COMPANY_STATUS_INVALID;
        } else if (
          response === AddContactAsIamUserForCompanyStatus.ADD_CONTACT_AS_IAM_USER_FOR_COMPANY_STATUS_SUCCEEDED
        ) {
          // success.  Stop retrying.
          return response;
        } else if (response === AddContactAsIamUserForCompanyStatus.ADD_CONTACT_AS_IAM_USER_FOR_COMPANY_STATUS_FAILED) {
          // failure.  Stop retrying.
          return response;
        }
        // throw error to poll again
        throw false;
      }),
      retry({ delay: 2000 }),
      switchMap((_) => this.crmAPIService.getMultiContact({ namespace: namespace, crmObjectIds: [contactId] })),
      map((resp: GetMultiCrmObjectResponse) => resp?.crmObjects?.[0]),
      map((contact) => this.convertContactToRecipient(contact)),
    );
  }

  private isRecipientUserForCompany(contactId: string, companyId: string): Observable<boolean> {
    return this.platformService.isContactIamUserForCompany(contactId, companyId);
  }

  addRecipientAsUserOnAccount(accountGroupId: string, namespace: string, recipient: Recipient): Observable<Recipient> {
    if (RECIPIENT_STRATEGY === RecipientStrategy.CRMContact) {
      return this.accountGroupService
        .getMulti(
          [accountGroupId],
          new ProjectionFilter({
            accountGroupExternalIdentifiers: true,
          }),
          new ReadFilter({
            includeDeleted: true,
          }),
        )
        .pipe(
          map((accountGroups) => accountGroups?.[0]?.externalIdentifiers?.platformCrmCompanyId ?? ''),
          switchMap((companyId) =>
            this.isRecipientUserForCompany(recipient.id, companyId).pipe(
              map((isContact) => {
                return { isContact: isContact, companyId: companyId };
              }),
            ),
          ),
          switchMap((result) =>
            iif(
              () => result.isContact && !!recipient.userID,
              of(recipient),
              this.addContactAsIAMUser(recipient.id, result.companyId, namespace),
            ),
          ),
        );
    } else {
      return of(recipient);
    }
  }

  private retrieveRecipientsUsingAssociatedContacts(
    accountGroupId: string,
    namespace: string,
  ): Observable<Recipient[]> {
    const companyId$ = this.accountGroupService
      .getMulti(
        [accountGroupId],
        new ProjectionFilter({
          accountGroupExternalIdentifiers: true,
        }),
        new ReadFilter({
          includeDeleted: true,
        }),
      )
      .pipe(map((accountGroups) => accountGroups?.[0]?.externalIdentifiers?.platformCrmCompanyId ?? ''));

    const contactAssociations$ = companyId$.pipe(
      switchMap((companyId) =>
        this.crmAssociationApiService.listAssociation({
          namespace: namespace,
          pagingOptions: {
            pageSize: 100,
          },
          filtersV2: {
            operator: FilterGroupOperator.FILTER_GROUP_OPERATOR_AND,
            filters: [
              {
                fieldId: 'to_id',
                operator: FilterOperator.FILTER_OPERATOR_IS,
                values: [
                  {
                    string: companyId,
                  },
                ],
              },
              {
                fieldId: 'association_type',
                operator: FilterOperator.FILTER_OPERATOR_IS,
                values: [
                  {
                    string: 'Contact-Company',
                  },
                ],
              },
            ],
          },
        } as ListAssociationsRequestInterface),
      ),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 408) {
          throw false;
        }
        return EMPTY;
      }),
      retry({ delay: 1000 }),
      map((resp) => resp?.associations?.map((r) => r.fromId) || []),
    );

    const contacts$: Observable<CrmObject[]> = contactAssociations$.pipe(
      switchMap((ids) => {
        if (ids?.length === 0) {
          return of([]);
        }

        return this.crmAPIService
          .getMultiContact({
            namespace: namespace,
            crmObjectIds: ids,
          })
          .pipe(map((res) => res.crmObjects));
      }),
    );

    return contacts$.pipe(
      map((contacts) => {
        return contacts
          .filter((c) => JSON.stringify(c) !== '{}')
          .map((contact) => this.convertContactToRecipient(contact));
      }),
    );
  }

  private retrieveRecipientsUsingAccountGroupUsers(accountGroupId: string, partnerId: string): Observable<Recipient[]> {
    return this.accountService.loadUsers(accountGroupId, partnerId).pipe(
      map((users: User[]) => {
        return users.map((user) => convertUserToRecipient(user));
      }),
    );
  }

  retrieveRecipients(accountGroupId: string, partnerId: string): Observable<Recipient[]> {
    if (RECIPIENT_STRATEGY === RecipientStrategy.CRMContact) {
      return this.retrieveRecipientsUsingAssociatedContacts(accountGroupId, partnerId);
    } else {
      return this.retrieveRecipientsUsingAccountGroupUsers(accountGroupId, partnerId);
    }
  }

  private openCreateCRMContactModal(req: AddRecipientToAccountRequest): void {
    const ref = this.dialog.open(CrmCreateCrmObjectModalComponent, {
      data: {
        objectType: 'Contact',
      },
      injector: createCrmObjectModalProviders('Contact', this.injector),
      width: '500px',
    });

    ref
      .afterClosed()
      .pipe(take(1))
      .subscribe({
        next: (contact: CrmCreateCrmObjectModalResult) => {
          this.associateContact(contact.crmObject.crmObjectId, req.accountGroupId, req.partnerId);
        },
      });
  }

  private async associateContact(contactId: string, accountGroupId: string, namespace): Promise<void> {
    const association$ = this.accountGroupService
      .getMulti(
        [accountGroupId],
        new ProjectionFilter({
          accountGroupExternalIdentifiers: true,
        }),
        new ReadFilter({
          includeDeleted: true,
        }),
      )
      .pipe(
        map((accountGroups) => accountGroups?.[0]?.externalIdentifiers?.platformCrmCompanyId ?? ''),
        map((companyId) => {
          return {
            associationType: 'Contact-Company',
            fromId: contactId,
            toId: companyId,
            tags: [],
          };
        }),
      );

    const associationCall$ = association$.pipe(
      switchMap((association: Association) => {
        return this.crmAssociationApiService.createAssociation({
          namespace: namespace,
          association: association,
        });
      }),
      map((_) => contactId),
      catchError((err) => (err.status === 409 ? contactId : '')),
    );

    await firstValueFrom(associationCall$).then((_) => {
      const result: AssociatedContact = {
        contactId: contactId,
        namespace: namespace,
      };

      this.contactAssociated$$.next(result);
    });
  }

  private openAddCRMContactModal(req: AddRecipientToAccountRequest): void {
    const dialogRef = this.dialog.open(SelectObjectTypeModalComponent, {
      data: {
        baseColumnIds: [],
        modalTitle: this.translate.instant('BILLING.INVOICE_HEADER.ADD_RECIPIENT'),
        objectType: 'Contact',
        associations: [],
        hideCreateButton: false,
        enableMultiplePreSelectedRows: false,
        singleSelection: true,
        filters: [
          {
            fieldId: this.fieldService.getFieldId(StandardExternalIds.Email),
            filterId: 'filterContactByEmail',
            operator: GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_EMPTY,
          },
        ],
      } as ListObjectTypeDialogData,
      injector: Injector.create({
        providers: [
          {
            provide: PageAnalyticsInjectionToken,
            useFactory: () => {
              const deps = {
                trackEvent(): void {
                  return;
                },
              };
              return deps;
            },
          },
          {
            provide: GalaxyFilterChipInjectionToken,
            useFactory: tableFiltersInjectionTokenGenerator('Contact'),
          },
        ],
        parent: this.injector,
      }),
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: ListObjectTypeDialogResult) => {
        if (result?.clickedCreate) {
          this.openCreateCRMContactModal(req);
        }

        if (result?.selectedRows?.length > 0) {
          this.associateContact(result?.selectedRows?.[0]?.id, req.accountGroupId, req.partnerId);
        }
      });
  }

  private openUserModal(req: AddRecipientToAccountRequest): void {
    const users = req?.existingRecipientsOnAccount?.map((recipient) => convertRecipientToUser(recipient)) || [];
    const dialogRef: MatDialogRef<AddUserDialogComponent> = this.dialog.open(AddUserDialogComponent, {
      width: '620px',
    });
    dialogRef.componentInstance.accountGroupId = req.accountGroupId;
    dialogRef.componentInstance.partnerId = req.partnerId;
    dialogRef.componentInstance.existingUsers = users;
    dialogRef.componentInstance.showCreateTab = true;
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        const recipient = convertUserToRecipient(result?.users?.[0]);
        this.selectionResult$$.next(recipient);
      });
  }

  addRecipientsToAccount(
    accountGroupId: string,
    partnerId: string,
    existingRecipientsOnAccount?: Recipient[],
  ): Observable<Recipient> {
    const r: AddRecipientToAccountRequest = {
      accountGroupId: accountGroupId,
      partnerId: partnerId,
      existingRecipientsOnAccount: existingRecipientsOnAccount,
    };

    this.addRecipientInterfaceRequested$$.next(r);

    return this.selectionResult$$.pipe(take(1));
  }
}
